/**
 * Platform Module API
 * 
 * WHY: The Platform API is the ONLY gateway for all inter-module
 * communication per the architecture mandate. Mission management
 * and audit logging are platform-level concerns.
 */

import { http } from '../core/client'
import type { Mission, CreateMissionPayload, UpdateMissionPayload } from '../core/types'

/**
 * Mission management endpoints.
 * All mission operations route through the Platform Gateway.
 */
export const missionsApi = {
  /**
   * List all missions accessible to the current user.
   */
  list: () => http.get<Mission[]>('/missions'),

  /**
   * Get a specific mission by ID.
   */
  get: (id: string) => http.get<Mission>(`/missions/${id}`),

  /**
   * Create a new mission.
   * WHY: Mission creation is a critical audit event - the compliance
   * headers from the core client ensure proper attribution.
   */
  create: (data: CreateMissionPayload) => 
    http.post<Mission>('/missions', data),

  /**
   * Update an existing mission.
   * WHY: Supports mission lifecycle management - status changes, renames, etc.
   */
  update: (id: string, data: UpdateMissionPayload) =>
    http.put<Mission>(`/missions/${id}`, data),

  /**
   * Delete a mission.
   * WHY: Cleanup completed or cancelled missions.
   */
  delete: (id: string) => http.delete(`/missions/${id}`),
}

/**
 * Platform health and readiness endpoints.
 * WHY: These are used by the UI to show system status
 * and by Kubernetes for liveness/readiness probes.
 */
export const platformApi = {
  /**
   * Get platform information and version.
   */
  info: () => http.get<{ version: string; name: string }>('/', { skipMissionContext: true }),

  /**
   * Health check endpoint.
   */
  health: () => http.get<{ status: string }>('/health', { skipMissionContext: true }),

  /**
   * Readiness check endpoint.
   */
  ready: () => http.get<{ status: string; dependencies: Record<string, boolean> }>('/ready', { skipMissionContext: true }),
}
